// Horario semanal fijo de entreno por equipo, en formato calendario (de un
// vistazo, como el calendario en papel de la escuela). A partir de estos
// horarios el backend crea automáticamente el registro de "Entrenamientos"
// del día (en estado "pendiente") en cuanto termina la sesión, y avisa por
// push al entrenador para que pase asistencia o marque que se suspendió.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Alert,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Add as AddIcon, Save as SaveIcon, Schedule as ScheduleIcon } from "@mui/icons-material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { jwtDecode } from "jwt-decode";

import apiFetch from "services/http";
import { useSeason } from "context/SeasonContext";
import { PageToolbar, ModalShell, ConfirmDialog, EmptyState, useNotify } from "components/Convokko";
import { DEPORTES } from "config";

const DIAS_SEMANA = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

// Franja del calendario: de 15:30 a 22:30, en bloques de media hora.
const HORA_INICIO_GRID = 15 * 60 + 30;
const HORA_FIN_GRID = 22 * 60 + 30;
const PX_POR_MIN = 1.5;
const ALTURA_GRID = (HORA_FIN_GRID - HORA_INICIO_GRID) * PX_POR_MIN;
const ANCHO_COL_HORA = 44;
const ANCHO_MIN_DIA = 104;
// En movil las columnas se encogen para que quepan los 5 dias sin scroll
// horizontal (en vez de forzar el ancho de escritorio en una pantalla mucho
// mas estrecha).
const ANCHO_COL_HORA_XS = 26;
const ANCHO_MIN_DIA_XS = 58;

const MARCAS = [];
for (let m = HORA_INICIO_GRID; m <= HORA_FIN_GRID; m += 30) MARCAS.push(m);

// Las horas de entreno siempre son en punto o y media: se eligen de una
// lista en vez de escribirlas, con un poco de margen sobre la franja visible
// del calendario por si algún entreno empieza o acaba justo en el borde.
const OPCIONES_HORA = [];
for (let m = 15 * 60; m <= 23 * 60; m += 30) OPCIONES_HORA.push(m);

// Tonos derivados de la marca (naranja/azul marino) en vez de un arcoíris de
// colores sin relación entre sí: cada equipo se distingue igual, pero el
// conjunto se ve coherente con el resto de la app.
const PALETA_EQUIPOS = [
  "#F59E0B",
  "#22336B",
  "#E87C29",
  "#33477F",
  "#B45309",
  "#5C6BC0",
  "#FBB53F",
  "#1A2752",
  "#C2760F",
  "#7A88B8",
];

const aMinutos = (hhmm) => {
  const [h, m] = (hhmm || "0:0").split(":").map(Number);
  return h * 60 + m;
};

const formatoHora = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const horarioVacio = {
  _id: null,
  id_equipo: "",
  dia_semana: 0,
  hora_inicio: "",
  hora_fin: "",
};

export default function HorariosList() {
  const notify = useNotify();
  const { temporada } = useSeason();
  const theme = useTheme();
  const esMovil = useMediaQuery(theme.breakpoints.down("sm"));
  const anchoColHora = esMovil ? ANCHO_COL_HORA_XS : ANCHO_COL_HORA;
  const anchoMinDia = esMovil ? ANCHO_MIN_DIA_XS : ANCHO_MIN_DIA;

  const [horarios, setHorarios] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [userRole, setUserRole] = useState("entrenador");

  const [equipoFiltro, setEquipoFiltro] = useState("");
  const [deporteFiltro, setDeporteFiltro] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentHorario, setCurrentHorario] = useState(horarioVacio);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    try {
      const decoded = jwtDecode(localStorage.getItem("token"));
      setUserRole(decoded.rol || "entrenador");
    } catch (e) {
      setUserRole("entrenador");
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [dataHorarios, dataEquipos] = await Promise.all([
        apiFetch("/api/horarios"),
        apiFetch("/api/equipos"),
      ]);
      setHorarios(Array.isArray(dataHorarios) ? dataHorarios : []);
      setEquipos(Array.isArray(dataEquipos) ? dataEquipos : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const equiposById = useMemo(() => {
    const map = {};
    equipos.forEach((e) => {
      map[e._id || e.id] = e;
    });
    return map;
  }, [equipos]);

  const equiposTemporada = useMemo(
    () =>
      [...equipos]
        .filter((e) => Number(e.temporada) === Number(temporada))
        .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [equipos, temporada]
  );

  const colorEquipo = useMemo(() => {
    const map = {};
    equiposTemporada.forEach((eq, idx) => {
      map[eq._id || eq.id] = PALETA_EQUIPOS[idx % PALETA_EQUIPOS.length];
    });
    return map;
  }, [equiposTemporada]);

  // Fútbol sala y volley entrenan en instalaciones distintas: se ven como
  // calendarios separados (una pestaña por deporte) en vez de mezclados.
  const deportesDisponibles = useMemo(() => {
    const presentes = new Set(equiposTemporada.map((e) => e.deporte).filter(Boolean));
    return DEPORTES.filter((d) => presentes.has(d.value));
  }, [equiposTemporada]);

  useEffect(() => {
    if (deportesDisponibles.length && !deportesDisponibles.some((d) => d.value === deporteFiltro)) {
      setDeporteFiltro(deportesDisponibles[0].value);
    }
  }, [deportesDisponibles, deporteFiltro]);

  const equiposDeporte = useMemo(
    () =>
      deportesDisponibles.length > 1
        ? equiposTemporada.filter((e) => e.deporte === deporteFiltro)
        : equiposTemporada,
    [equiposTemporada, deporteFiltro, deportesDisponibles]
  );

  const handleChangeDeporte = (_e, nuevo) => {
    if (!nuevo) return;
    setDeporteFiltro(nuevo);
    setEquipoFiltro("");
  };

  const horariosVisibles = useMemo(() => {
    const idsVisibles = new Set(equiposDeporte.map((e) => e._id || e.id));
    return horarios.filter((h) => {
      if (!idsVisibles.has(h.id_equipo)) return false;
      if (equipoFiltro && h.id_equipo !== equipoFiltro) return false;
      return true;
    });
  }, [horarios, equiposDeporte, equipoFiltro]);

  // Eventos agrupados por día (0=lunes...6=domingo), con carriles asignados
  // para que los horarios que se solapan en el tiempo se vean uno al lado
  // del otro en vez de superpuestos.
  const eventosPorDia = useMemo(() => {
    const porDia = Array.from({ length: 7 }, () => []);
    horariosVisibles.forEach((h) => {
      const equipo = equiposById[h.id_equipo];
      if (!equipo || h.dia_semana < 0 || h.dia_semana > 6) return;
      porDia[h.dia_semana].push({ ...h, equipo });
    });

    return porDia.map((lista) => {
      const ordenada = [...lista].sort((a, b) => aMinutos(a.hora_inicio) - aMinutos(b.hora_inicio));

      // El ancho de cada horario solo debe repartirse con los que de verdad
      // se solapan con él (su "grupo"), no con todos los del día: si a las
      // 17:30 coinciden dos equipos, un horario a las 20:00 que no se cruza
      // con nadie debe seguir ocupando el ancho completo.
      const resultado = [];
      let grupoActual = [];
      let carrilesFin = [];
      let carrilesGrupo = 0;

      const cerrarGrupo = () => {
        const totalCarriles = carrilesGrupo || 1;
        grupoActual.forEach((ev) => resultado.push({ ...ev, totalCarriles }));
        grupoActual = [];
        carrilesFin = [];
        carrilesGrupo = 0;
      };

      ordenada.forEach((ev) => {
        const inicio = aMinutos(ev.hora_inicio);
        const fin = aMinutos(ev.hora_fin);

        // ningún carril sigue "vivo" a esta hora -> el grupo anterior ya
        // terminó del todo, empieza uno nuevo desde cero
        if (grupoActual.length > 0 && !carrilesFin.some((finCarril) => finCarril > inicio)) {
          cerrarGrupo();
        }

        let carril = carrilesFin.findIndex((finCarril) => finCarril <= inicio);
        if (carril === -1) {
          carril = carrilesFin.length;
          carrilesFin.push(fin);
        } else {
          carrilesFin[carril] = fin;
        }
        carrilesGrupo = Math.max(carrilesGrupo, carrilesFin.length);
        grupoActual.push({ ...ev, carril });
      });
      cerrarGrupo();

      return resultado;
    });
  }, [horariosVisibles, equiposById]);

  // El entreno casi nunca cae en sábado o domingo: se ocultan por defecto
  // para no desperdiciar dos columnas vacías, pero si algún horario real
  // cae ahí, la columna se muestra igualmente (nunca se esconde un dato).
  const diasVisibles = useMemo(() => {
    const dias = [0, 1, 2, 3, 4];
    if (eventosPorDia[5]?.length) dias.push(5);
    if (eventosPorDia[6]?.length) dias.push(6);
    return dias;
  }, [eventosPorDia]);

  // --- CRUD ---
  const handleOpenCreate = () => {
    setEditMode(false);
    setCurrentHorario({
      ...horarioVacio,
      // solo se autorrellena cuando el entrenador lleva un único equipo; con
      // varios, tiene que elegir explícitamente a cuál pertenece el horario
      id_equipo:
        userRole === "entrenador" && equiposDeporte.length === 1
          ? equiposDeporte[0]?._id || ""
          : "",
    });
    setOpenModal(true);
  };

  const handleOpenEdit = (horario) => {
    setEditMode(true);
    setCurrentHorario({ ...horarioVacio, ...horario });
    setOpenModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentHorario((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeHoraInicio = (e) => {
    const nuevaInicio = e.target.value;
    setCurrentHorario((prev) => ({
      ...prev,
      hora_inicio: nuevaInicio,
      // si la hora de fin ya no tiene sentido con la nueva de inicio, se
      // adelanta automáticamente media hora para no dejar el formulario roto
      hora_fin: prev.hora_fin && prev.hora_fin > nuevaInicio ? prev.hora_fin : "",
    }));
  };

  const handleSave = async () => {
    if (!currentHorario.id_equipo) {
      notify.error("Selecciona un equipo");
      return;
    }
    if (!currentHorario.hora_inicio || !currentHorario.hora_fin) {
      notify.error("Indica la hora de inicio y de fin");
      return;
    }
    if (currentHorario.hora_fin <= currentHorario.hora_inicio) {
      notify.error("La hora de fin debe ser posterior a la de inicio");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        id_equipo: currentHorario.id_equipo,
        dia_semana: Number(currentHorario.dia_semana),
        hora_inicio: currentHorario.hora_inicio,
        hora_fin: currentHorario.hora_fin,
        ...(editMode && currentHorario._id && { _id: currentHorario._id }),
      };
      if (editMode) {
        await apiFetch("/api/horarios/actualizar-horario", { method: "PUT", body: payload });
        notify.success("Horario actualizado");
      } else {
        await apiFetch("/api/horarios", { method: "POST", body: payload });
        notify.success("Horario creado");
      }
      setOpenModal(false);
      await fetchData();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await apiFetch("/api/horarios/eliminar-horario", {
        method: "DELETE",
        params: { horario_id: deleteTarget._id },
      });
      setHorarios((prev) => prev.filter((h) => h._id !== deleteTarget._id));
      notify.success("Horario eliminado");
      setDeleteTarget(null);
      setOpenModal(false);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <PageToolbar
          icon={ScheduleIcon}
          title="Horarios de entreno"
          count={horariosVisibles.length}
          countLabel={horariosVisibles.length === 1 ? "horario" : "horarios"}
          action={{
            label: "Nuevo horario",
            shortLabel: "Nuevo",
            icon: <AddIcon />,
            onClick: handleOpenCreate,
          }}
          filters={
            userRole === "coordinador" || equiposDeporte.length > 1
              ? [
                  {
                    id: "equipo",
                    label: "Equipo",
                    allLabel: "Todos los equipos",
                    value: equipoFiltro,
                    onChange: setEquipoFiltro,
                    options: equiposDeporte.map((e) => ({
                      value: e._id || e.id,
                      label: e.nombre,
                    })),
                    minWidth: 130,
                  },
                ]
              : []
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {deportesDisponibles.length > 1 && (
          <ToggleButtonGroup
            value={deporteFiltro}
            exclusive
            onChange={handleChangeDeporte}
            size="small"
            sx={{ mb: 2 }}
          >
            {deportesDisponibles.map((d) => (
              <ToggleButton key={d.value} value={d.value} sx={{ fontWeight: 700, px: 2 }}>
                {d.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        {loading ? (
          <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} />
        ) : equiposTemporada.length === 0 ? (
          <EmptyState
            icon={ScheduleIcon}
            title="No hay equipos para configurar"
            subtitle="No se encontraron equipos en la temporada seleccionada."
          />
        ) : (
          <Box
            sx={{
              overflowX: "auto",
              border: "1px solid",
              borderColor: "grey.200",
              borderRadius: 3,
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{
                display: "flex",
                minWidth: anchoColHora + diasVisibles.length * anchoMinDia,
              }}
            >
              <Box sx={{ width: anchoColHora, flexShrink: 0 }} />
              {diasVisibles.map((idx) => (
                <Box
                  key={idx}
                  sx={{
                    flex: "1 1 0",
                    minWidth: anchoMinDia,
                    textAlign: "center",
                    py: 0.95,
                    borderLeft: "1px solid rgba(255,255,255,0.12)",
                    bgcolor: "secondary.main",
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={800}
                    textTransform="uppercase"
                    fontSize="0.65rem"
                    color="#fff"
                    sx={{ letterSpacing: 0.4 }}
                  >
                    {DIAS_SEMANA[idx].slice(0, 3)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                minWidth: anchoColHora + diasVisibles.length * anchoMinDia,
              }}
            >
              <Box
                sx={{
                  width: anchoColHora,
                  flexShrink: 0,
                  position: "relative",
                  height: ALTURA_GRID,
                }}
              >
                {MARCAS.map((m) => (
                  <Typography
                    key={m}
                    variant="caption"
                    sx={{
                      position: "absolute",
                      top: (m - HORA_INICIO_GRID) * PX_POR_MIN - 7,
                      right: esMovil ? 3 : 6,
                      fontSize: esMovil ? "0.5rem" : "0.6rem",
                      fontWeight: m % 60 === 0 ? 700 : 400,
                      color: "text.secondary",
                    }}
                  >
                    {formatoHora(m)}
                  </Typography>
                ))}
              </Box>

              {diasVisibles.map((idx) => (
                <Box
                  key={idx}
                  sx={{
                    flex: "1 1 0",
                    minWidth: anchoMinDia,
                    position: "relative",
                    height: ALTURA_GRID,
                    borderLeft: "1px solid",
                    borderColor: "grey.200",
                    bgcolor: "background.paper",
                  }}
                >
                  {MARCAS.map((m) => (
                    <Box
                      key={m}
                      sx={{
                        position: "absolute",
                        top: (m - HORA_INICIO_GRID) * PX_POR_MIN,
                        left: 0,
                        right: 0,
                        borderTop: "1px solid",
                        borderColor: m % 60 === 0 ? "grey.200" : "grey.100",
                      }}
                    />
                  ))}

                  {eventosPorDia[idx].map((ev) => {
                    const inicioMin = Math.min(
                      Math.max(aMinutos(ev.hora_inicio), HORA_INICIO_GRID),
                      HORA_FIN_GRID
                    );
                    const finMin = Math.min(
                      Math.max(aMinutos(ev.hora_fin), HORA_INICIO_GRID),
                      HORA_FIN_GRID
                    );
                    const top = (inicioMin - HORA_INICIO_GRID) * PX_POR_MIN;
                    const height = Math.max((finMin - inicioMin) * PX_POR_MIN, 24);
                    const color = colorEquipo[ev.id_equipo] || PALETA_EQUIPOS[0];
                    return (
                      <Tooltip
                        key={ev._id}
                        title={`${ev.equipo.nombre} · ${ev.hora_inicio}–${ev.hora_fin}`}
                      >
                        <Box
                          onClick={() => handleOpenEdit(ev)}
                          sx={{
                            position: "absolute",
                            top,
                            height,
                            left: `calc(${(ev.carril / ev.totalCarriles) * 100}% + 3px)`,
                            width: `calc(${100 / ev.totalCarriles}% - 6px)`,
                            bgcolor: color,
                            color: "#fff",
                            borderRadius: 2,
                            px: 0.75,
                            py: 0.4,
                            overflow: "hidden",
                            cursor: "pointer",
                            boxShadow: "0 2px 5px rgba(34,51,107,0.22)",
                            transition: "transform .1s ease, filter .1s ease",
                            "&:hover": {
                              filter: "brightness(1.08)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.62rem",
                              fontWeight: 700,
                              lineHeight: 1.15,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "block",
                            }}
                          >
                            {ev.equipo.nombre}
                          </Typography>
                          <Typography sx={{ fontSize: "0.56rem", opacity: 0.9, display: "block" }}>
                            {ev.hora_inicio}–{ev.hora_fin}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Fab
        color="primary"
        aria-label="Nuevo horario"
        onClick={handleOpenCreate}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          color: "#fff",
          display: { xs: "flex", sm: "none" },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Crear / editar horario */}
      <ModalShell
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={editMode ? "Editar horario" : "Nuevo horario"}
        subtitle={editMode ? equiposById[currentHorario.id_equipo]?.nombre : ""}
        icon={ScheduleIcon}
        actions={
          <>
            {editMode && (
              <Button
                onClick={() => setDeleteTarget(currentHorario)}
                sx={{ color: "error.main", mr: "auto" }}
              >
                Eliminar
              </Button>
            )}
            <Button onClick={() => setOpenModal(false)} sx={{ color: "text.secondary" }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon />}
              disabled={
                saving ||
                ((userRole === "coordinador" || equiposDeporte.length > 1) &&
                  !editMode &&
                  !currentHorario.id_equipo)
              }
              sx={{ borderRadius: 2 }}
            >
              {saving ? "Guardando..." : editMode ? "Guardar cambios" : "Crear"}
            </Button>
          </>
        }
      >
        <Stack spacing={2.5} mt={0.5}>
          {(userRole === "coordinador" || equiposDeporte.length > 1) && (
            <FormControl fullWidth required>
              <InputLabel>Equipo</InputLabel>
              <Select
                name="id_equipo"
                value={currentHorario.id_equipo || ""}
                label="Equipo"
                onChange={handleChange}
                disabled={editMode}
                sx={{ height: 44 }}
              >
                {equiposDeporte.map((eq) => (
                  <MenuItem key={eq._id} value={eq._id}>
                    {eq.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth required>
            <InputLabel>Día de la semana</InputLabel>
            <Select
              name="dia_semana"
              value={currentHorario.dia_semana}
              label="Día de la semana"
              onChange={handleChange}
              sx={{ height: 44 }}
            >
              {DIAS_SEMANA.map((label, idx) => (
                <MenuItem key={label} value={idx}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth required>
              <InputLabel>Hora de inicio</InputLabel>
              <Select
                name="hora_inicio"
                value={currentHorario.hora_inicio || ""}
                label="Hora de inicio"
                onChange={handleChangeHoraInicio}
                sx={{ height: 44 }}
              >
                {OPCIONES_HORA.map((m) => (
                  <MenuItem key={m} value={formatoHora(m)}>
                    {formatoHora(m)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required disabled={!currentHorario.hora_inicio}>
              <InputLabel>Hora de fin</InputLabel>
              <Select
                name="hora_fin"
                value={currentHorario.hora_fin || ""}
                label="Hora de fin"
                onChange={handleChange}
                sx={{ height: 44 }}
              >
                {OPCIONES_HORA.filter(
                  (m) => formatoHora(m) > (currentHorario.hora_inicio || "")
                ).map((m) => (
                  <MenuItem key={m} value={formatoHora(m)}>
                    {formatoHora(m)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Ese día, unos 15 minutos antes de la hora de inicio, se creará automáticamente el
            registro en &quot;Entrenamientos&quot; y se avisará por notificación para pasar
            asistencia o marcar que se ha suspendido.
          </Alert>
        </Stack>
      </ModalShell>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Eliminar horario"
        message={
          <>
            ¿Seguro que quieres eliminar el horario de los{" "}
            <b>{DIAS_SEMANA[deleteTarget?.dia_semana]}</b> ({deleteTarget?.hora_inicio}–
            {deleteTarget?.hora_fin})? Esta acción no se puede deshacer.
          </>
        }
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
        loading={saving}
      />

      <Footer />
    </DashboardLayout>
  );
}
